import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  FormCheckbox,
  FormTextInput
} from "protocol-setup/components/FormComponents";
import { postWithAuth } from "protocol-setup/redux/makeFetch";
import {
  defaultErrorMessage,
  uatSetupUrl
} from "protocol-setup/redux/constants";
import {
  OpenErrorNotification,
  OpenSuccessNotification
} from "protocol-setup/components/notification";
import ButtonGroupSignoff from "protocol-setup/components/FormComponents/ButtonGroupSignoff";
import { dispatchRefreshEvent, useSetupPermission } from "protocol-setup/helpers";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import { tabNames } from "../orHelpers";

export const handleSubmitForm = async (
  data,
  requestNumber,
  onSuccessCallback
) => {
  try {
    const payload = {
      requestNumber,
      isOpenAdimnSetupCompleted: data.isOpenAdminSetupCompleted,
      formIdsList: data.formIdsList,
      testSitesList: data.testSitesList
    };

    await postWithAuth(uatSetupUrl, payload);

    if (onSuccessCallback) {
      await onSuccessCallback();
    }

    OpenSuccessNotification({
      message: "UAT Setup successfully submitted.",
      autoHide: true
    });
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
  }
};

export const uatSchema = yup.object().shape({
  isOpenAdminSetupCompleted: yup
    .boolean()
    .oneOf([true], "UAT Setup completion must be confirmed."),
  formIdsList: yup
    .string()
    .required("IDs are required")
    .max(100, "Cannot exceed 100 characters."),
  testSitesList: yup
    .string()
    .required("Test sites are required.")
    .max(100, "Cannot exceed 100 characters.")
});

const UATSetupForm = ({ protocolSetupData, fetchPsData, readOnly }) => {
  const { requestNumber, uatSetup } = protocolSetupData || {};

  const hasSignOffPermission = useSetupPermission(ProtectionElements.OPEN_SIGNOFF_UATSETUP);
  const isSignOffEditable = !readOnly && hasSignOffPermission;

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isSubmitting, dirtyFields }
  } = useForm({
    resolver: yupResolver(uatSchema),
    defaultValues: {
      isOpenAdminSetupCompleted: uatSetup?.isOpenAdimnSetupCompleted,
      formIdsList: uatSetup?.formIdsList || "",
      testSitesList: uatSetup?.testSitesList || ""
    }
  });

  const onSuccess = async () => {
    await fetchPsData();
    dispatchRefreshEvent();
  };

  // Wrap handleSubmitForm with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback) =>
      handleSubmitForm(data, requestNumber, onSuccessCallback),
    dirtyFields,
    tabNames.SIGNOFF_UAT_SETUP,
    "Signoff"
  );

  const onSubmit = (data) => {
    if (readOnly) return;
    const d = { ...data, requestNumber };
    onSubmitWithHistory(d, onSuccess);
  };

  const onReset = () => {
    reset();
  };

  // Memoize the form values to prevent unnecessary resets
  const formValues = useMemo(() => ({
    isOpenAdminSetupCompleted: uatSetup?.isOpenAdimnSetupCompleted,
    formIdsList: uatSetup?.formIdsList || "",
    testSitesList: uatSetup?.testSitesList || ""
  }), [uatSetup]);

  // Reset form when uatSetup data loads/changes
  useEffect(() => {
      reset(formValues);
  }, [ formValues, reset]);

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${
        readOnly ? "read-only" : ""
      }`}
    >
      <form
        id="UATSetupForm"
        className="bg-transparent"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>

          <ul>
            <li className="row mb-0">
              <div className="col-8 mr-4 mb-0">
                <FormCheckbox
                  className="pl-0 mb-3 d-block"
                  labelClassName="color-dark-blue"
                  name="isOpenAdminSetupCompleted"
                  section="openRave"
                  form="uatSetupForm"
                  control={control}
                  required
                  disabled={!isSignOffEditable}
                  wrapperClassName={!isSignOffEditable ? 'read-only-show' : ''}
                  trigger={trigger}
                />
              </div>
            </li>
            <li className="row">
              <div className="col-4 mr-4 mb-0">
                <FormTextInput
                  name="formIdsList"
                  section="openRave"
                  form="uatSetupForm"
                  control={control}
                  trigger={trigger}
                  required
                  disabled={!isSignOffEditable}
                />
              </div>
              <div className="col-4 mr-4 mb-0">
                <FormTextInput
                  name="testSitesList"
                  section="openRave"
                  form="uatSetupForm"
                  control={control}
                  trigger={trigger}
                  required
                  disabled={!isSignOffEditable}
                />
              </div>
            </li>
          </ul>
        </div>
        <div className="form-layout-footer px-0 pb-0">
          {isSignOffEditable && (
            <ButtonGroupSignoff
              onSubmit={handleSubmitForm}
              onReset={onReset}
              isSubmitting={isSubmitting}
              control={control}
              protectionElement= {ProtectionElements.OPEN_SIGNOFF_UATSETUP }
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default UATSetupForm;
